import { upsertTrackingLink } from '../../tracking-links-api';
import { invalidateBrandLinks } from '../../useBrandLinks';
import { saveLinkFor, type LinkForContext } from './link-for';

/**
 * Pointing a made-for asset's tracking link at its landing page (see link-for.ts), shared by the library (at the
 * page's first save) and the composer (a retry on open, on publish, or from its Try again).
 *
 * One call per page at a time: the composer opens on the new page while the library's call may still be in flight,
 * and joins it instead of sending a second one. A success marks the stored context `linked` (the link is the durable
 * record from then on); a failure leaves it unmarked, so the next open retries.
 */

const inflight = new Map<string, Promise<boolean>>();

const keyOf = (tenantId: number, pageId: string): string => `${tenantId}:${pageId}`;

/** Point the asset version's link at the page (with no address while the page is not live: spark-creatives builds the
 *  link from the page's address once it is published). Resolves whether spark-creatives took it; never rejects. */
export const sendLinkHere = (tenantId: number, pageId: string, ctx: LinkForContext, liveUrl = ''): Promise<boolean> => {
  const key = keyOf(tenantId, pageId);
  const held = inflight.get(key);
  if (held) return held;
  const run = upsertTrackingLink(tenantId, {
    creative_id: ctx.creativeId,
    version_key: ctx.versionKey,
    kind: ctx.kind,
    name: ctx.name,
    destination_type: 'landing_page',
    landing_page_id: pageId,
    destination_url: liveUrl,
  })
    .then((res) => {
      if (res.ok) {
        saveLinkFor(tenantId, pageId, { ...ctx, linked: true });
        invalidateBrandLinks(tenantId);
      }
      return res.ok;
    })
    .catch(() => false)
    .finally(() => {
      inflight.delete(key);
    });
  inflight.set(key, run);
  return run;
};

/** Whether a pointing for the page is in flight (the links read then may not show it yet). */
export const sendingLinkHere = (tenantId: number, pageId: string): boolean => inflight.has(keyOf(tenantId, pageId));
